import KpiCard from '@dashboard/components/KpiCard'
import type { CheckoutAnalyticsResponse } from '@dashboard/types/analytics.types'
import { calcSettlementDelta, calcTrendDelta, formatSettlementTime } from '@dashboard/utils/analyticsHelpers'
import cn from '@integrations/classnames'
import { formatMoney, tr } from 'mmfintech-commons'
import skeletonStyles from '../DashboardSkeleton/DashboardSkeleton.module.scss'
import styles from './KpiCards.module.scss'

interface Props {
  analytics: CheckoutAnalyticsResponse | null
  isLoading: boolean
}

const RATE_ROWS = [
  { key: 'completed', trKey: 'FRONTEND.DASHBOARD.SUCCESS_RATE.COMPLETED', trDefault: 'Completed' },
  { key: 'pending', trKey: 'FRONTEND.DASHBOARD.SUCCESS_RATE.PENDING', trDefault: 'Pending' },
  { key: 'failed', trKey: 'FRONTEND.DASHBOARD.SUCCESS_RATE.FAILED', trDefault: 'Failed' }
] as const

function KpiCards({ analytics, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className={cn(styles['kpi-strip'], 'kpi-strip')}>
        {[1, 2, 3, 4].map((i) => (
          <KpiCard key={i} className={cn('kpi-card--skeleton')}>
            <div
              className={cn(
                styles['kpi-skeleton-line'],
                skeletonStyles['dashboard-skeleton'],
                skeletonStyles['dashboard-skeleton-animated'],
                'kpi-skeleton-line',
                styles['kpi-skeleton-line--short'],
                'kpi-skeleton-line--short'
              )}
            />
            <div
              className={cn(
                styles['kpi-skeleton-line'],
                skeletonStyles['dashboard-skeleton'],
                skeletonStyles['dashboard-skeleton-animated'],
                'kpi-skeleton-line',
                styles['kpi-skeleton-line--tall'],
                'kpi-skeleton-line--tall'
              )}
            />
          </KpiCard>
        ))}
      </div>
    )
  }

  const curr = analytics?.currentPeriod?.summary
  const prev = analytics?.previousPeriod?.summary

  const countDelta = curr && prev ? calcTrendDelta(curr.totalCount, prev.totalCount) : null
  const avgAmtDelta = curr && prev ? calcTrendDelta(curr.averageTransactionAmount, prev.averageTransactionAmount) : null
  const settleDelta =
    curr && prev ? calcSettlementDelta(curr.avgSettlementTimeInSeconds, prev.avgSettlementTimeInSeconds) : null

  const currency = analytics?.currentPeriod?.currency ?? ''
  const avgAmount = curr ? formatMoney(curr.averageTransactionAmount, currency) : null
  const avgAmountFigure =
    avgAmount && currency && avgAmount.endsWith(currency) ? avgAmount.slice(0, -currency.length).trim() : avgAmount
  const rateCounts = {
    completed: curr?.completedCount ?? 0,
    pending: curr?.pendingCount ?? 0,
    failed: curr?.failedCount ?? 0
  }

  return (
    <div className={cn(styles['kpi-strip'], 'kpi-strip')}>
      <KpiCard>
        <KpiCard.Header
          label={tr('FRONTEND.DASHBOARD.KPI.INCOMING_TRANSACTIONS', 'Incoming Transactions')}
          info={tr(
            'FRONTEND.DASHBOARD.KPI.INCOMING_TRANSACTIONS_INFO',
            'Number of payments received in the selected period.'
          )}
        />
        <KpiCard.Value>{curr ? curr.totalCount : '—'}</KpiCard.Value>
        <KpiCard.Trend delta={countDelta} />
      </KpiCard>

      <KpiCard>
        <KpiCard.Header
          label={tr('FRONTEND.DASHBOARD.KPI.AVG_TRANSACTION', 'Avg. Transaction')}
          info={tr(
            'FRONTEND.DASHBOARD.KPI.AVG_TRANSACTION_INFO',
            'Average amount of a received payment in the selected period.'
          )}
        />
        <KpiCard.Value unit={avgAmount && avgAmountFigure !== avgAmount ? currency : undefined}>
          {avgAmountFigure ?? '—'}
        </KpiCard.Value>
        <KpiCard.Trend delta={avgAmtDelta} />
      </KpiCard>

      <KpiCard>
        <KpiCard.Header
          label={tr('FRONTEND.DASHBOARD.KPI.AVG_SETTLEMENT', 'Avg. Settlement')}
          info={tr(
            'FRONTEND.DASHBOARD.KPI.AVG_SETTLEMENT_INFO',
            'Average time between a payment being received and the funds being settled.'
          )}
        />
        <KpiCard.Value>{curr ? formatSettlementTime(curr.avgSettlementTimeInSeconds) : '—'}</KpiCard.Value>
        <KpiCard.Trend delta={settleDelta} />
      </KpiCard>

      <KpiCard>
        <KpiCard.Header label={tr('FRONTEND.DASHBOARD.SUCCESS_RATE.TITLE', 'Success Rate')} />
        <div className={cn(styles['kpi-card__split'], 'kpi-card__split')}>
          <div className={cn(styles['kpi-card__split-main'], 'kpi-card__split-main')}>
            <KpiCard.Value>{curr && curr.totalCount > 0 ? `${Math.round(curr.successRate ?? 0)}%` : '—'}</KpiCard.Value>
            <span className={cn(styles['kpi-card__trend-label'], 'kpi-card__trend-label')}>
              {curr?.totalCount ?? 0} {tr('FRONTEND.DASHBOARD.TRANSACTIONS_SHORT', 'txns')} ·{' '}
              {tr('FRONTEND.DASHBOARD.SUCCESS_RATE.SUBTITLE', 'Last 7 days')}
            </span>
          </div>
          <div className={cn(styles['kpi-card__breakdown'], 'kpi-card__breakdown')}>
            {RATE_ROWS.map((row) => (
              <div key={row.key} className={cn(styles['kpi-card__breakdown-row'], 'kpi-card__breakdown-row')}>
                <span
                  className={cn(
                    styles['kpi-card__breakdown-dot'],
                    'kpi-card__breakdown-dot',
                    styles[`kpi-card__breakdown-dot--${row.key}`],
                    `kpi-card__breakdown-dot--${row.key}`
                  )}
                />
                <span className={cn(styles['kpi-card__breakdown-label'], 'kpi-card__breakdown-label')}>
                  {tr(row.trKey, row.trDefault)}
                </span>
                <b>{rateCounts[row.key]}</b>
              </div>
            ))}
          </div>
        </div>
      </KpiCard>
    </div>
  )
}

export default KpiCards
